import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, RefreshCw, ExternalLink, List, Activity } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { apiClient } from '@/services/api';
import { errorLog } from '@/utils/logger';

interface CoinReward {
  id: string;
  userId: string;
  workSessionId: string;
  coinsEarned: number;
  transactionHash?: string;
  walletAddress: string;
  status: 'pending' | 'confirming' | 'sent' | 'failed';
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    fullName: string;
    email: string;
  };
  workSession?: {
    date: string;
    durationMinutes: number;
  };
}

interface Transaction {
  id: string;
  userId: string;
  workSessionId: string;
  coinsEarned: number;
  transactionHash?: string;
  walletAddress: string;
  status: string;
  errorMessage?: string;
  retryCount: number;
  lastRetryAt?: string;
  confirmationsReceived?: number;
  blockNumber?: string;
  payoutRequestId?: string;
  payoutStatus?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    fullName: string;
    email: string;
  };
}

interface NonceStatus {
  dbNonce: number | null;
  blockchainNonce: number;
  inSync: boolean;
  address: string;
}

const RewardManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [pendingRewards, setPendingRewards] = useState<CoinReward[]>([]);
  const [failedRewards, setFailedRewards] = useState<CoinReward[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txSummary, setTxSummary] = useState<Record<string, number>>({});
  const [txStatusFilter, setTxStatusFilter] = useState<string>('');
  const [nonceStatus, setNonceStatus] = useState<NonceStatus | null>(null);
  const [isResettingNonce, setIsResettingNonce] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<'pending' | 'failed' | 'transactions' | 'nonce'>('pending');

  useEffect(() => {
    fetchRewardData();
  }, []);

  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
    }
  }, [activeTab, txStatusFilter]);

  const fetchRewardData = async () => {
    try {
      setIsLoading(true);

      const [pendingRes, failedRes, nonceRes] = await Promise.allSettled([
        apiClient.rewards.getPending(),
        apiClient.rewards.getFailed(),
        apiClient.rewards.getNonceStatus(),
      ]);

      if (pendingRes.status === 'fulfilled' && pendingRes.value.success) {
        setPendingRewards(pendingRes.value.data || []);
      }

      if (failedRes.status === 'fulfilled' && failedRes.value.success) {
        setFailedRewards(failedRes.value.data || []);
      }

      if (nonceRes.status === 'fulfilled' && nonceRes.value.success) {
        setNonceStatus(nonceRes.value.data);
      }
    } catch (error: any) {
      errorLog('Failed to fetch reward data:', error);
      toast.error('Failed to load reward data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const params: { status?: string; limit: number } = { limit: 50 };
      if (txStatusFilter) {
        params.status = txStatusFilter;
      }
      const res = await apiClient.rewards.getTransactions(params);
      if (res.success) {
        setTransactions(res.data.transactions || []);
        setTxSummary(res.data.summary || {});
      }
    } catch (error: any) {
      errorLog('Failed to fetch transactions:', error);
    }
  };

  const handleRetryDistribution = async (rewardId: string) => {
    try {
      setRetryingIds(prev => new Set([...prev, rewardId]));

      const response = await apiClient.rewards.retry(rewardId);
      if (response.success) {
        toast.success('Reward distribution retried successfully!');
        await fetchRewardData();
        if (activeTab === 'transactions') {
          await fetchTransactions();
        }
      } else {
        toast.error(response.message || 'Failed to retry distribution');
      }
    } catch (error: any) {
      errorLog('Failed to retry distribution:', error);
      toast.error(error.message || 'Failed to retry distribution');
    } finally {
      setRetryingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(rewardId);
        return newSet;
      });
    }
  };

  const handleNonceReset = async () => {
    if (!confirm('Reset the nonce to match blockchain state? This is an emergency action.')) {
      return;
    }
    try {
      setIsResettingNonce(true);
      const res = await apiClient.rewards.resetNonce();
      if (res.success) {
        toast.success('Nonce reset successfully');
        setNonceStatus(res.data);
      }
    } catch (error: any) {
      errorLog('Failed to reset nonce:', error);
      toast.error(error.message || 'Failed to reset nonce');
    } finally {
      setIsResettingNonce(false);
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirming: 'bg-blue-100 text-blue-800',
      sent: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const renderRewardCard = (reward: CoinReward) => (
    <div
      key={reward.id}
      className={`bg-white rounded-lg border-2 p-4 sm:p-6 ${
        reward.status === 'failed' ? 'border-red-200' : 'border-yellow-200'
      }`}
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-medium text-gray-900">
              {reward.user?.fullName || 'Unknown User'}
            </span>
            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
              reward.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {reward.status === 'pending' ? 'Pending' : 'Failed'}
            </span>
          </div>

          <div className="text-sm text-gray-600 space-y-1">
            <p><strong>Email:</strong> {reward.user?.email}</p>
            <p><strong>Amount:</strong> {reward.coinsEarned} HEP</p>
            <p>
              <strong>Wallet:</strong>
              <span className="font-mono ml-1">{formatAddress(reward.walletAddress)}</span>
            </p>
            {reward.workSession && (
              <p>
                <strong>Work Session:</strong> {format(parseISO(reward.workSession.date), 'MMM d, yyyy')}
                ({formatDuration(reward.workSession.durationMinutes)})
              </p>
            )}
            <p><strong>Created:</strong> {format(parseISO(reward.createdAt), 'MMM d, h:mm a')}</p>
            {reward.transactionHash && (
              <p>
                <strong>Tx Hash:</strong>
                <Link
                  to={`/explorer?hash=${reward.transactionHash}`}
                  className="font-mono text-blue-600 hover:text-blue-700 ml-1 inline-flex items-center gap-1"
                >
                  {formatAddress(reward.transactionHash)}
                  <ExternalLink size={12} />
                </Link>
              </p>
            )}
            {reward.errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-800">
                  <strong>Error:</strong> {reward.errorMessage}
                </p>
              </div>
            )}
          </div>
        </div>

        {reward.status === 'failed' && (
          <div className="flex gap-3">
            <button
              onClick={() => handleRetryDistribution(reward.id)}
              disabled={retryingIds.has(reward.id)}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium touch-manipulation"
            >
              <RefreshCw size={16} />
              {retryingIds.has(reward.id) ? 'Retrying...' : 'Retry Distribution'}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderTransactionsTab = () => (
    <div className="space-y-4">
      {/* Summary counts */}
      {Object.keys(txSummary).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Object.entries(txSummary).map(([status, count]) => (
            <div key={status} className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 uppercase">{status}</p>
              <p className="text-xl font-bold text-gray-900">{count}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-700">Filter by status:</label>
        <select
          value={txStatusFilter}
          onChange={(e) => setTxStatusFilter(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
        >
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="confirming">Confirming</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
        </select>
      </div>

      {/* Transaction list */}
      {transactions.length === 0 ? (
        <div className="text-center py-8">
          <List size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No transactions found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="pb-2 pr-4">User</th>
                <th className="pb-2 pr-4">Amount</th>
                <th className="pb-2 pr-4">Status</th>
                <th className="pb-2 pr-4">Tx Hash</th>
                <th className="pb-2 pr-4">Retries</th>
                <th className="pb-2 pr-4">Confirmations</th>
                <th className="pb-2 pr-4">Created</th>
                <th className="pb-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transactions.map((tx) => (
                <tr key={tx.id} className="text-gray-700">
                  <td className="py-3 pr-4">
                    <div>{tx.user?.fullName || 'Unknown'}</div>
                    <div className="text-xs text-gray-400">{tx.user?.email}</div>
                  </td>
                  <td className="py-3 pr-4 font-medium">{tx.coinsEarned} HEP</td>
                  <td className="py-3 pr-4">
                    <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getStatusBadge(tx.status)}`}>
                      {tx.status}
                    </span>
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs">
                    {tx.transactionHash ? formatAddress(tx.transactionHash) : '-'}
                  </td>
                  <td className="py-3 pr-4 text-center">
                    {tx.retryCount > 0 ? (
                      <span className="text-orange-600 font-medium">{tx.retryCount}</span>
                    ) : '-'}
                  </td>
                  <td className="py-3 pr-4 text-center">
                    {tx.confirmationsReceived != null ? tx.confirmationsReceived : '-'}
                  </td>
                  <td className="py-3 pr-4 text-xs text-gray-500">
                    {format(parseISO(tx.createdAt), 'MMM d, h:mm a')}
                  </td>
                  <td className="py-3">
                    {tx.status === 'failed' && (
                      <button
                        onClick={() => handleRetryDistribution(tx.id)}
                        disabled={retryingIds.has(tx.id)}
                        className="text-blue-600 hover:text-blue-700 text-xs font-medium disabled:opacity-50"
                      >
                        {retryingIds.has(tx.id) ? 'Retrying...' : 'Retry'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin')}
          className="p-2 text-gray-500 hover:text-gray-700 touch-manipulation"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-4xl font-bold text-white">Reward Management</h1>
          <p className="text-lg text-white mt-1">Monitor and manage cryptocurrency distributions</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap">
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'pending'
                  ? 'border-yellow-500 text-yellow-600 bg-yellow-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Pending ({pendingRewards.length})
            </button>
            <button
              onClick={() => setActiveTab('failed')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'failed'
                  ? 'border-red-500 text-red-600 bg-red-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Failed ({failedRewards.length})
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'transactions'
                  ? 'border-purple-500 text-purple-600 bg-purple-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Transactions
            </button>
            <button
              onClick={() => setActiveTab('nonce')}
              className={`px-6 py-4 text-sm font-medium border-b-2 ${
                activeTab === 'nonce'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Nonce
            </button>
            <div className="ml-auto p-4">
              <button
                onClick={() => { fetchRewardData(); if (activeTab === 'transactions') fetchTransactions(); }}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                Refresh
              </button>
            </div>
          </nav>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading reward data...</p>
            </div>
          ) : activeTab === 'pending' ? (
            <div className="space-y-4">
              {pendingRewards.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto text-green-400 mb-4" />
                  <p className="text-gray-500 text-lg mb-2">No pending rewards</p>
                  <p className="text-sm text-gray-400">All rewards have been processed.</p>
                </div>
              ) : (
                <>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-medium text-yellow-900 mb-1">Pending Distributions</h3>
                    <p className="text-sm text-yellow-800">
                      These rewards are waiting to be processed by the blockchain service.
                    </p>
                  </div>
                  {pendingRewards.map(renderRewardCard)}
                </>
              )}
            </div>
          ) : activeTab === 'failed' ? (
            <div className="space-y-4">
              {failedRewards.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle size={48} className="mx-auto text-green-400 mb-4" />
                  <p className="text-gray-500 text-lg mb-2">No failed rewards</p>
                  <p className="text-sm text-gray-400">All distributions have been successful.</p>
                </div>
              ) : (
                <>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="font-medium text-red-900 mb-1">Failed Distributions</h3>
                    <p className="text-sm text-red-800">
                      These rewards failed to process. Review the error messages and retry if needed.
                    </p>
                  </div>
                  {failedRewards.map(renderRewardCard)}
                </>
              )}
            </div>
          ) : activeTab === 'transactions' ? (
            renderTransactionsTab()
          ) : (
            <div className="space-y-6">
              {/* Nonce Status */}
              <div className={`border rounded-lg p-6 ${
                nonceStatus
                  ? nonceStatus.inSync
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                  : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Activity className={`h-6 w-6 ${nonceStatus?.inSync ? 'text-green-600' : 'text-red-600'}`} />
                    <h3 className="text-lg font-semibold">Nonce Status</h3>
                  </div>
                  <button
                    onClick={handleNonceReset}
                    disabled={isResettingNonce}
                    className="text-sm bg-red-600 text-white px-3 py-1.5 rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {isResettingNonce ? 'Resetting...' : 'Emergency Reset'}
                  </button>
                </div>
                {nonceStatus ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">DB Nonce</p>
                      <p className="text-lg font-semibold">{nonceStatus.dbNonce ?? 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Blockchain Nonce</p>
                      <p className="text-lg font-semibold">{nonceStatus.blockchainNonce}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Status</p>
                      <p className={`text-lg font-semibold ${nonceStatus.inSync ? 'text-green-600' : 'text-red-600'}`}>
                        {nonceStatus.inSync ? 'In Sync' : 'Out of Sync'}
                      </p>
                    </div>
                    <div className="sm:col-span-3">
                      <p className="text-gray-600">Distributor Address</p>
                      <p className="font-mono text-sm">{nonceStatus.address}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500">Unable to load nonce status</p>
                )}
              </div>

              {/* Distribution Statistics */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h4 className="font-medium text-gray-900 mb-3">Distribution Statistics</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Pending Rewards</p>
                    <p className="text-lg font-semibold text-yellow-600">{pendingRewards.length}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Failed Rewards</p>
                    <p className="text-lg font-semibold text-red-600">{failedRewards.length}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Total Pending Amount</p>
                    <p className="text-lg font-semibold text-blue-600">
                      {pendingRewards.reduce((acc, r) => acc + r.coinsEarned, 0)} HEP
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardManagementPage;
